import { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { generateInviteToken, hashToken } from '@/lib/inviteToken';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';

interface EmployeeOption {
  id: string;
  fullName: string;
  email: string;
  department: string;
}

type InviteMode = 'all' | 'department';

export interface CultureInviteFormProps {
  /** Campaña activa de cultura (`assessment_campaigns` tipo CULTURE). Sin campaña no se pueden enviar invitaciones. */
  activeCultureCampaignId: string | null;
  /** Tras enviar invitaciones correctamente (para refrescar listas en el padre). */
  onInvitesSent?: () => void;
}

export function CultureInviteForm({ activeCultureCampaignId, onInvitesSent }: CultureInviteFormProps) {
  const { appUser } = useAuth();
  const companyId = appUser?.companyId ?? null;

  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [inviteMode, setInviteMode] = useState<InviteMode>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [excludedEmployeeIds, setExcludedEmployeeIds] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [failedEmails, setFailedEmails] = useState<string[]>([]);

  useEffect(() => {
    async function loadEmployees() {
      if (!companyId) {
        setEmployees([]);
        setLoadingEmployees(false);
        return;
      }

      setLoadingEmployees(true);
      setError(null);
      try {
        const snapshot = await getDocs(
          query(
            collection(db, 'employees'),
            where('companyId', '==', companyId),
            where('isActive', '==', true)
          )
        );

        const parsed: EmployeeOption[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as Partial<{
            firstName: string;
            lastName: string;
            email: string;
            department: string;
          }>;
          const email = data.email?.trim();
          if (!email) return;

          const firstName = data.firstName?.trim() ?? '';
          const lastName = data.lastName?.trim() ?? '';
          const fullName = `${firstName} ${lastName}`.trim() || 'Colaborador';

          parsed.push({
            id: docSnap.id,
            fullName,
            email,
            department: data.department?.trim() || 'No especificado',
          });
        });

        parsed.sort((a, b) => a.fullName.localeCompare(b.fullName, 'es'));
        setEmployees(parsed);
      } catch (err) {
        console.error('Error cargando empleados para invitaciones de cultura:', err);
        setError('No se pudo cargar el directorio de empleados.');
      } finally {
        setLoadingEmployees(false);
      }
    }

    void loadEmployees();
  }, [companyId]);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((employee) => employee.department))).sort((a, b) => a.localeCompare(b, 'es')),
    [employees]
  );

  const baseSelection = useMemo(() => {
    if (inviteMode === 'department') {
      if (!selectedDepartment) return [];
      return employees.filter((employee) => employee.department === selectedDepartment);
    }
    return employees;
  }, [employees, inviteMode, selectedDepartment]);

  const selectedEmployees = useMemo(
    () => baseSelection.filter((employee) => !excludedEmployeeIds.has(employee.id)),
    [baseSelection, excludedEmployeeIds]
  );

  function toggleEmployee(employeeId: string) {
    setExcludedEmployeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }

  async function handleSendInvitations() {
    if (!companyId) {
      setError('No se encontró la empresa asociada.');
      return;
    }
    if (!activeCultureCampaignId) {
      setError('Debes tener una campaña de cultura activa. Créala en Desarrollo Organizacional → Cultura Organizacional.');
      return;
    }
    if (inviteMode === 'department' && !selectedDepartment) {
      setError('Selecciona un departamento para continuar.');
      return;
    }
    if (selectedEmployees.length === 0) {
      setError('No hay empleados seleccionados para invitar.');
      return;
    }

    setError(null);
    setSuccess(null);
    setFailedEmails([]);
    setIsSending(true);
    setProgress({ processed: 0, total: selectedEmployees.length });

    const sendEmail = httpsCallable(getFunctions(), 'sendAssessmentEmail');
    const failed: string[] = [];
    try {
      for (const employee of selectedEmployees) {
        try {
          const token = generateInviteToken();
          const tokenHash = await hashToken(token);
          const link = `${window.location.origin}/culture/${token}`;

          const inviteRef = await addDoc(collection(db, 'culture_invites'), {
            companyId,
            campaignId: activeCultureCampaignId,
            employeeId: employee.id,
            employeeName: employee.fullName,
            email: employee.email,
            department: employee.department || 'No especificado',
            targetType: 'employee',
            tokenHash,
            status: 'pending',
            createdAt: serverTimestamp(),
          });
          await setDoc(doc(db, 'culture_invite_token_lookup', tokenHash), {
            inviteTokenHash: tokenHash,
            inviteId: inviteRef.id,
            companyId,
            createdAt: serverTimestamp(),
          });

          await sendEmail({
            toEmail: employee.email,
            employeeName: employee.fullName,
            assessmentUrl: link,
            testType: 'Cultura Organizacional',
          });
        } catch (employeeError) {
          console.error(`Error enviando invitación a ${employee.email}:`, employeeError);
          failed.push(employee.email);
        } finally {
          setProgress((prev) => ({ ...prev, processed: prev.processed + 1 }));
        }
      }

      if (failed.length === 0) {
        setSuccess(`Invitaciones enviadas correctamente a ${selectedEmployees.length} empleados.`);
      } else {
        setFailedEmails(failed);
        setSuccess(
          `Proceso finalizado: ${selectedEmployees.length - failed.length} enviadas y ${failed.length} con error.`
        );
      }
      onInvitesSent?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'No se pudieron enviar las invitaciones.');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invitaciones</CardTitle>
        <CardDescription>
          Envío exclusivo para empleados activos del directorio de la empresa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!activeCultureCampaignId && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            Activa una campaña de cultura en Desarrollo Organizacional (pestaña Cultura Organizacional) para poder enviar
            invitaciones. Cada campaña conserva su propio historial de respuestas.
          </div>
        )}
        {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
        {success && (
          <div className="rounded-md bg-green-100 p-3 text-sm text-green-800 dark:bg-green-950/30 dark:text-green-300">
            {success}
          </div>
        )}

        <fieldset className="space-y-2">
          <Label>Modo de envío</Label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="culture-invite-mode"
                value="all"
                checked={inviteMode === 'all'}
                onChange={() => setInviteMode('all')}
                disabled={isSending}
              />
              Invitar a toda la empresa
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="culture-invite-mode"
                value="department"
                checked={inviteMode === 'department'}
                onChange={() => setInviteMode('department')}
                disabled={isSending}
              />
              Invitar por Departamento
            </label>
          </div>
        </fieldset>

        {inviteMode === 'department' && (
          <div className="space-y-2">
            <Label htmlFor="culture-department-select">Departamento</Label>
            <select
              id="culture-department-select"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={selectedDepartment}
              onChange={(event) => setSelectedDepartment(event.target.value)}
              disabled={isSending || loadingEmployees}
            >
              <option value="">Selecciona un departamento</option>
              {departments.map((department) => (
                <option key={department} value={department}>
                  {department}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="rounded-md border">
          <div className="border-b bg-muted/30 px-3 py-2 text-sm">
            Empleados seleccionados: <strong>{selectedEmployees.length}</strong> de {baseSelection.length}
          </div>
          <div className="max-h-64 overflow-auto">
            {loadingEmployees ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">Cargando empleados...</p>
            ) : baseSelection.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">No hay empleados para este filtro.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-3 py-2 text-left font-medium">Enviar</th>
                    <th className="px-3 py-2 text-left font-medium">Empleado</th>
                    <th className="px-3 py-2 text-left font-medium">Correo</th>
                    <th className="px-3 py-2 text-left font-medium">Departamento</th>
                  </tr>
                </thead>
                <tbody>
                  {baseSelection.map((employee) => {
                    const checked = !excludedEmployeeIds.has(employee.id);
                    return (
                      <tr key={employee.id} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEmployee(employee.id)}
                            disabled={isSending}
                          />
                        </td>
                        <td className="px-3 py-2">{employee.fullName}</td>
                        <td className="px-3 py-2">{employee.email}</td>
                        <td className="px-3 py-2">{employee.department}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {isSending && (
          <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
            Enviando invitaciones... {progress.processed}/{progress.total}
          </div>
        )}

        {failedEmails.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            Correos con error: {failedEmails.join(', ')}
          </div>
        )}

        <Button
          type="button"
          onClick={() => void handleSendInvitations()}
          disabled={
            isSending || loadingEmployees || selectedEmployees.length === 0 || !activeCultureCampaignId
          }
        >
          {isSending ? 'Procesando invitaciones...' : 'Enviar Invitaciones por Correo'}
        </Button>
      </CardContent>
    </Card>
  );
}
